import type { VariationSetWithImages } from "@/components/variation-set-results";
import {
  DEFAULT_AD_ASPECT_RATIO,
  isAspectRatio,
  type AspectRatio,
} from "@shared/aspectRatios";

export const ITERATION_SELECTION_LIMIT = 10;
export const IMAGE_ITERATION_VARIANT_COUNT = 7;
export const VIDEO_ITERATION_VARIANT_COUNT = 4;
export type IterationMediaType = "image" | "video";
export type IterationDateRange = "today" | "last_7_days" | "last_14_days" | "last_30_days" | "all_time";
export type IterationSelectable = { id: string; mediaType?: string | null; category?: string; eligibleIterationSource?: boolean };
export type IterationSourceMeta = { sourceName?: string; sourceAdId?: string; sourceImageUrl?: string; sourceVideoUrl?: string; sourceVideoPreviewUrl?: string; sourceStatus?: string; mediaType?: string };
export type IterationRunSummary = {
  id: string;
  status: string;
  mediaType?: string | null;
  aspectRatio?: string | null;
  createdAt?: string | Date | null;
  sourceCount?: number;
  readyCount?: number;
  totalCount?: number;
  generatedAdIds?: unknown;
};

export function iterationRunSourceCount(run: IterationRunSummary): number {
  return typeof run.sourceCount === "number"
    ? run.sourceCount
    : Array.isArray(run.generatedAdIds)
      ? run.generatedAdIds.length
      : 0;
}

export function formatIterationRunDate(value: string | Date | null | undefined): string {
  if (!value) return "Date unavailable";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function creativeIterationSeedHref(brandId: string, adId: string): string {
  return `/brands/${brandId}/creative-iterations?seedAdId=${encodeURIComponent(adId)}`;
}

export function creativeIterationExternalSeedHref(brandId: string, sourceId: string): string {
  return `/brands/${brandId}/creative-iterations?seedPerformanceSourceId=${encodeURIComponent(sourceId)}`;
}

export function removeCreativeIterationSeed(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("seedAdId");
  params.delete("seedPerformanceSourceId");
  return params.toString();
}

export function resolveCreativeIterationSeed<T extends IterationSelectable>(
  items: T[],
  seedAdId: string | null,
): T | null {
  if (!seedAdId) return null;
  return items.find(
    (item) =>
      item.id === seedAdId &&
      item.eligibleIterationSource === true &&
      (!item.mediaType || item.mediaType === "image" || item.mediaType === "video"),
  ) ?? null;
}

export function resolveFreshCreativeIterationSeed<T extends IterationSelectable>(input: {
  items: T[] | undefined;
  seedAdId: string | null;
  isSuccess: boolean;
  isFetching: boolean;
}): T | null {
  if (!input.isSuccess || input.isFetching || !Array.isArray(input.items)) return null;
  return resolveCreativeIterationSeed(input.items, input.seedAdId);
}

export function shouldRestoreLatestIterationRun(input: {
  seedAdId: string | null;
  seedConsumed: boolean;
  hasLatest: boolean;
  hasRun: boolean;
  dismissedLatest: boolean;
}): boolean {
  return !input.seedAdId &&
    !input.seedConsumed &&
    input.hasLatest &&
    !input.hasRun &&
    !input.dismissedLatest;
}

export function buildCreativeIterationRunPayload<T extends { id: string }>(
  selected: T[],
  requestKey: string,
  guidance: Record<string, string>,
  aspectRatio: AspectRatio,
) {
  const generatedAdIds = selected.filter((item: any) => item.sourceType !== "performance").map((item) => item.id);
  const performanceSourceIds = selected.filter((item: any) => item.sourceType === "performance").map((item) => item.id);
  return {
    generatedAdIds,
    ...(performanceSourceIds.length ? { performanceSourceIds } : {}),
    requestKey,
    aspectRatio,
    creativeGuidanceByAdId: Object.fromEntries(
      selected
        .map((item) => [item.id, guidance[item.id]?.trim() || ""] as const)
        .filter(([, value]) => value)
        .sort(([a], [b]) => a.localeCompare(b)),
    ),
  };
}

/** Uses a source ratio only when every selected image agrees on a supported value. */
export function defaultCreativeIterationAspectRatio(
  selected: Array<{ aspectRatio?: string | null }>,
): AspectRatio {
  const ratios = new Set(selected.map((item) => item.aspectRatio));
  return ratios.size === 1 && isAspectRatio(selected[0]?.aspectRatio)
    ? selected[0].aspectRatio
    : DEFAULT_AD_ASPECT_RATIO;
}

export function iterationMediaType(item: Pick<IterationSelectable, "mediaType" | "category">): IterationMediaType | null {
  if (item.category === "slideshows" || item.mediaType === "slideshow") return null;
  if (item.mediaType === "video") return "video";
  if (!item.mediaType || item.mediaType === "image") return "image";
  return null;
}

export function iterationSelectionMediaType<T extends IterationSelectable>(
  selected: Iterable<T>,
): IterationMediaType | null {
  const media = new Set([...selected].map(iterationMediaType).filter((value): value is IterationMediaType => !!value));
  return media.size === 1 ? [...media][0] : null;
}

export const iterationVariantCount = (mediaType: IterationMediaType) =>
  mediaType === "video" ? VIDEO_ITERATION_VARIANT_COUNT : IMAGE_ITERATION_VARIANT_COUNT;

/**
 * Returns local-calendar boundaries as precise instants, so the API's inclusive
 * `createdAt >= from && createdAt <= to` predicate includes the entire current day.
 */
export function iterationDateBounds(range: IterationDateRange, now = new Date()): { from?: string; to?: string } {
  if (range === "all_time") return {};
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const daysBack = range === "today"
    ? 0
    : range === "last_7_days"
      ? 6
      : range === "last_14_days"
        ? 13
        : 29;
  start.setDate(start.getDate() - daysBack);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return { from: start.toISOString(), to: end.toISOString() };
}

export function toggleIterationSelection<T extends IterationSelectable>(current: Map<string, T>, ad: T): { selection: Map<string, T>; limitReached: boolean; mediaMismatch?: boolean } {
  const selection = new Map(current);
  if (selection.has(ad.id)) { selection.delete(ad.id); return { selection, limitReached: false }; }
  const mediaType = iterationMediaType(ad);
  if (!mediaType) return { selection, limitReached: false };
  const selectedMediaType = iterationSelectionMediaType(selection.values());
  if (selectedMediaType && selectedMediaType !== mediaType) return { selection, limitReached: false, mediaMismatch: true };
  if (selection.size >= ITERATION_SELECTION_LIMIT) return { selection, limitReached: true };
  selection.set(ad.id, ad);
  return { selection, limitReached: false };
}
export const iterationConfirmationCount = (selected: number, mediaType: IterationMediaType = "image") =>
  selected * iterationVariantCount(mediaType);
export const iterationProgress = (sets: VariationSetWithImages[]) => ({
  ready: sets.reduce((n, s) => n + s.images.filter(i =>
    (i.status === "rendered" || i.status === "complete") &&
    (i.imageUrl || i.videoUrl)
  ).length, 0),
  total: sets.reduce((n, set) => n + (set.mediaType === "video"
    ? set.images.length
    : 8), 0),
});
export function sourceMetaByCardId(sourceCards: Array<{
  id: string;
  concept?: { sourceName?: string | null; sourceGeneratedAdId?: string | null; sourcePerformanceAdId?: string | null; mediaType?: string | null };
  render?: { imageUrl?: string | null; videoUrl?: string | null; videoPreviewUrl?: string | null };
  mediaType?: string | null;
  iterationStatus?: string | null;
}> = []) {
  return new Map(sourceCards.map((card) => [
    card.id,
    {
      sourceName: card.concept?.sourceName || "Source ad",
      sourceAdId: card.concept?.sourceGeneratedAdId || card.concept?.sourcePerformanceAdId || undefined,
      sourceImageUrl: card.render?.imageUrl || undefined,
      sourceVideoUrl: card.render?.videoUrl || undefined,
      sourceVideoPreviewUrl: card.render?.videoPreviewUrl || undefined,
      mediaType: card.mediaType || card.concept?.mediaType || undefined,
      sourceStatus: card.iterationStatus || undefined,
    },
  ]));
}